import React, { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Mail, Lock, Loader2, ShieldCheck, Eye, EyeOff, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Logo from "@/components/brand/Logo";
import ChangePasswordForm from "@/components/auth/ChangePasswordForm";
import { auth } from "@/lib/auth";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // "login" | "change"
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (auth.isAuthenticated()) navigate(from, { replace: true });
  }, [from, navigate]);

  if (auth.isAuthenticated()) {
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      const user = await auth.login({ email: email.trim(), password, remember });
      toast.success(`Welcome back, ${user.full_name || user.email}`);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not sign in — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950 p-4">
      <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full brand-gradient opacity-20 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-purple-400 opacity-20 blur-3xl" />
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo height={120} />
        </div>
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-3xl shadow-2xl shadow-indigo-500/10 border border-white/60 dark:border-white/10 p-8">
          {mode === "change" ? (
            <ChangePasswordForm initialEmail={email} onBack={() => setMode("login")} />
          ) : (
          <>
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 px-3 py-1 text-[10px] font-semibold tracking-widest uppercase text-indigo-700 dark:text-indigo-300">
              <ShieldCheck className="h-3 w-3" /> Secure sign-in
            </div>
            <h1 className="font-display font-extrabold text-2xl tracking-tight mt-3">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to your ISOQuest Management Portal.</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-9"
                  autoComplete="email"
                  autoFocus
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-xs">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 pr-10"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Checkbox
                checked={remember}
                onCheckedChange={v => setRemember(!!v)}
                data-testid="login-remember"
              />
              <span className="text-sm">Remember me on this device</span>
            </label>

            {error && (
              <div data-testid="login-error" className="rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-xs px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              className="w-full brand-gradient text-white font-semibold shadow-lg shadow-indigo-500/25"
              data-testid="login-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-200 dark:border-white/10 text-[11px] text-slate-500 leading-relaxed">
            <p className="flex items-start gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
              <span>Only email addresses invited by an ISOQuest administrator or consultant can sign in. If you haven't received an invite, please contact your account owner.</span>
            </p>
            <p className="mt-2">First time signing in? Enter the password you'd like to use — we'll save it to your account on first login.</p>
            <button
              type="button"
              onClick={() => { setError(""); setMode("change"); }}
              data-testid="login-change-password"
              className="mt-3 inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <KeyRound className="h-3 w-3" /> Change password
            </button>
          </div>
          </>
          )}
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-4">
          © ISOQuest Management Portal · Protected by email-invitation sign-in.
        </p>
      </div>
    </div>
  );
}
